import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import storage from 'app/services/storage';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { GROUP_DIVIDER, RESULT_FIELD_ID_PARTS_DIVIDER } from 'constants/documentExtractionFields';
import { AiDataRecognitionResult } from 'constants/documents';
import { PAGE_CODES } from 'constants/pageCodes';
import useCompanyId from 'hooks/useCompanyId';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { generateDocValidationSchema } from 'utils/validation';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { getCompanyProperties } from 'store/companies/selectors';
import { getDocumentExtractionFilledFieldsAction } from 'store/documents/actions';
import { getAiExtractionModelsGroupedByLayers } from 'store/documents/selectors';
import { selectIsGenAiDocument } from 'store/documents/selectors/documents.selectors';
import { openConfirmationDialogAction } from 'store/global/actions';
import { getDocumentExtractionFieldsAction } from 'store/system/actions';
import {
  getDocumentExtractionFieldsWithNamedType,
  getFieldsTreeGroupedByAiExtractionFieldName,
} from 'store/system/selectors';

import EditExtractionField from './EditExtractionField';
import {
  cloneNestedFields,
  prepareExtractionFields,
  removeDynamicGroupFromTree,
  setDefaultValuesForBooleanFields,
  setDefaultValuesForMultipleDropdownFields,
} from './EditExtractionFields.utils';
import {
  LOCAL_STORAGE_FORM_KEY,
  PROPERTY_AI_EXTRACTION_FIELD_CONFIDENCE_THRESHOLD,
} from './EditExtractionFieldsForm.constants';

const EditExtractionFieldsFormFieldsPart = ({
  documentTypeId = '',
  isView = true,
  docId = '',
  setIsLoading,
  isLoading,
  aiModelFieldToFillInForm = null,
  setAiModelFieldToFillInForm = null,
  boundingBoxPopUpInfo = null,
  form,
  initialForm = null,
  validationErrors,
  setSchema,
  setInForm,
  handleChange,
  setForm,
  validateField,
  duplicatedFields,
  setDuplicatedFields,
  isSkipDataLoading = false,
  setDocumentExtractionFieldsTree,
  documentExtractionFieldsTree,
  skipBooleanDefaults = false,
  renderFields = null,
}) => {
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const isSaveToLocalStorageAllowedRef = useRef(true);

  const companyId = useCompanyId();
  const companyProperties = useSelector(getCompanyProperties);
  const documentExtractionFields = useSelector(getDocumentExtractionFieldsWithNamedType);
  const fieldsTreeGroupedByAiExtractionFieldName = useSelector(
    getFieldsTreeGroupedByAiExtractionFieldName,
  );
  const aiAnalysisResult = useSelector(getAiExtractionModelsGroupedByLayers(docId));
  const isGenAi = useSelector(selectIsGenAiDocument(docId));

  const { isViewAllowed, isEditAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.DOCUMENT_EXTRACTION),
  );
  const { isManageAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_DOCUMENT_EXTRACTION_FIELDS),
  );

  const mappedAiFieldsResult = aiAnalysisResult
    ? aiAnalysisResult[AiDataRecognitionResult.MAPPED_AI_MODEL_FIELDS]
    : null;

  const aiExtractionFieldConfidenceThreshold = useMemo(
    () =>
      companyProperties[companyId]?.find(
        ({ propertyName }) => propertyName === PROPERTY_AI_EXTRACTION_FIELD_CONFIDENCE_THRESHOLD,
      ),
    [companyProperties, companyId],
  );

  useEffect(() => {
    const setFormToLocalStorage = () => {
      storage.setItem(LOCAL_STORAGE_FORM_KEY, formRef.current, true);
    };

    window.addEventListener('beforeunload', setFormToLocalStorage);

    return () => {
      if (isSaveToLocalStorageAllowedRef.current) {
        setFormToLocalStorage();
      }

      window.removeEventListener('beforeunload', setFormToLocalStorage);
    };
  }, []);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  useEffect(() => {
    prepareExtractionFields(
      documentExtractionFields,
      setDuplicatedFields,
      setSchema,
      setDocumentExtractionFieldsTree,
      setForm,
      mappedAiFieldsResult,
      fieldsTreeGroupedByAiExtractionFieldName,
      aiExtractionFieldConfidenceThreshold,
      initialForm,
      isGenAi,
      skipBooleanDefaults,
    );
  }, [
    documentExtractionFields,
    setForm,
    setSchema,
    mappedAiFieldsResult,
    fieldsTreeGroupedByAiExtractionFieldName,
    aiExtractionFieldConfidenceThreshold,
    setDuplicatedFields,
    initialForm,
    setDocumentExtractionFieldsTree,
    isGenAi,
    skipBooleanDefaults,
  ]);

  useEffectSafeStrictMode(() => {
    if (!documentTypeId || (!isViewAllowed && !isManageAllowed)) {
      return;
    }

    dispatch(getDocumentExtractionFieldsAction({ documentTypeId })).catch(Error);
  }, [documentTypeId, dispatch, isViewAllowed, isManageAllowed]);

  useEffectSafeStrictMode(() => {
    if (!docId || isSkipDataLoading) {
      return;
    }

    isSaveToLocalStorageAllowedRef.current = true;

    if (isView || !isViewAllowed) {
      return;
    }

    setIsLoading(true);

    dispatch(getDocumentExtractionFilledFieldsAction({ documentId: docId }))
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [isView, docId, dispatch, setIsLoading, isViewAllowed, isSkipDataLoading]);

  useEffect(() => {
    // Nothing picked from the AI pop-up: without this guard the effect wrote form[undefined] every
    // time the field tree loaded, which made an untouched form look edited (e.g. the worksheet
    // drawer asking to discard unsaved changes on close).
    if (!documentExtractionFieldsTree.length || _.isNil(aiModelFieldToFillInForm)) {
      return;
    }

    const { value, extractionFieldId, itemValues, type } = aiModelFieldToFillInForm;

    if (type?.type === 'Address') {
      const parent = documentExtractionFieldsTree.find(({ id }) => id === extractionFieldId);

      if (parent && parent.nestedFields) {
        const res = parent.nestedFields.reduce((acc, { id, name }) => {
          const lowerCasedName = _.toLower(name);

          if (itemValues[lowerCasedName]) {
            acc[id] = itemValues[lowerCasedName];
          }

          return acc;
        }, {});

        if (!_.isEmpty(res)) {
          Object.entries(res).forEach(([key, value]) => {
            setInForm(key, value);
          });
        }

        setAiModelFieldToFillInForm?.(null);

        return;
      }

      setInForm(extractionFieldId, value);

      return;
    }

    setInForm(extractionFieldId, value);
    setAiModelFieldToFillInForm?.(null);
  }, [
    fieldsTreeGroupedByAiExtractionFieldName,
    setInForm,
    documentExtractionFieldsTree,
    boundingBoxPopUpInfo?.value,
    aiModelFieldToFillInForm,
    setAiModelFieldToFillInForm,
  ]);

  const onAdd = useCallback(
    e => {
      const { id } = e.currentTarget.dataset;

      const handleField = item => {
        if (item.id !== id) {
          const nestedFieldsUpdate = item.nestedFields?.length
            ? { nestedFields: item.nestedFields.map(handleField) }
            : {};

          const nestedGroups = Object.entries(item.nestedGroups || {});

          const nestedGroupsUpdate = nestedGroups.length
            ? {
                nestedGroups: nestedGroups.reduce((acc, [key, fields]) => {
                  acc[key] = fields.map(handleField);

                  return acc;
                }, {}),
              }
            : {};

          return { ...item, ...nestedFieldsUpdate, ...nestedGroupsUpdate };
        }

        const groups = item.nestedGroups || {};
        const groupId = nanoid();

        const [parentIdBase, ...parentPostfixSplitted] = item.id.split(
          RESULT_FIELD_ID_PARTS_DIVIDER,
        );
        const parentPostfixJoined = parentPostfixSplitted.join(RESULT_FIELD_ID_PARTS_DIVIDER);
        const fieldIdPostfix = `${parentIdBase}${GROUP_DIVIDER}${groupId}${
          parentPostfixJoined ? `${RESULT_FIELD_ID_PARTS_DIVIDER}${parentPostfixJoined}` : ''
        }`;

        const { clonedStructuredFields, allClonedFields } = cloneNestedFields({
          fields: item.nestedFields,
          fieldIdPostfix,
        });

        const booleanFields = allClonedFields.reduce(setDefaultValuesForBooleanFields, {});
        const dropdownFields = allClonedFields.reduce(
          setDefaultValuesForMultipleDropdownFields,
          {},
        );

        setForm(prev => ({ ...prev, ...booleanFields, ...dropdownFields }));
        setDuplicatedFields([...duplicatedFields, ...allClonedFields]);
        setSchema(
          generateDocValidationSchema([
            ...documentExtractionFields,
            ...duplicatedFields,
            ...allClonedFields,
          ]),
        );

        return {
          ...item,
          nestedGroups: { ...groups, [groupId]: clonedStructuredFields },
        };
      };

      const tree = documentExtractionFieldsTree.map(handleField);

      setDocumentExtractionFieldsTree(tree);
    },
    [
      documentExtractionFields,
      documentExtractionFieldsTree,
      duplicatedFields,
      setDuplicatedFields,
      setForm,
      setSchema,
      setDocumentExtractionFieldsTree,
    ],
  );

  const onRemoveSubmit = useCallback(
    groupId => {
      const updatedFields = duplicatedFields.filter(({ id }) => !id.includes(groupId));

      const schema = generateDocValidationSchema([...documentExtractionFields, ...updatedFields]);
      const formWithoutDynamicGroup = _.omitBy(form, (__, key) =>
        key.includes(`${GROUP_DIVIDER}${groupId}`),
      );
      const documentExtractionFieldsWithoutDynamicGroup = removeDynamicGroupFromTree({
        groupId,
        fieldsTree: documentExtractionFieldsTree,
      });

      setDuplicatedFields(updatedFields);
      setSchema(schema);
      setForm(formWithoutDynamicGroup);
      setDocumentExtractionFieldsTree(documentExtractionFieldsWithoutDynamicGroup);
    },
    [
      documentExtractionFields,
      documentExtractionFieldsTree,
      duplicatedFields,
      form,
      setDuplicatedFields,
      setForm,
      setSchema,
      setDocumentExtractionFieldsTree,
    ],
  );

  const onRemove = useCallback(
    e => {
      const { groupId } = e.currentTarget.dataset;
      const isFilled = _.some(form, (value, key) => key.includes(groupId) && value);

      if (isFilled) {
        return dispatch(
          openConfirmationDialogAction({
            type: ConfirmationModalType.WARNING,
            message: 'Confirm Data Removal',
            description: 'Are you sure you want to delete this data?',
            onAccept: () => onRemoveSubmit(groupId),
            onAcceptButtonName: 'Yes',
          }),
        );
      }

      onRemoveSubmit(groupId);
    },
    [dispatch, form, onRemoveSubmit],
  );

  if (!documentExtractionFieldsTree.length) {
    return 'No fields available';
  }

  const isDisabled = isLoading || (!isEditAllowed && !isManageAllowed);

  // A caller with its own layout (the document worksheet) gets the loaded field tree and the
  // field props; the form state, validation and data loading above stay shared.
  if (renderFields) {
    return renderFields({
      fields: documentExtractionFieldsTree,
      disabled: isDisabled,
      errors: validationErrors,
      form,
      setInForm,
      validateField,
      onChange: handleChange,
      onAdd,
      onRemove,
    });
  }

  return (
    <>
      {documentExtractionFieldsTree.map(field => {
        const isInputHighlighted = field.aiExtractionFieldName === boundingBoxPopUpInfo?.type?.name;

        return (
          <EditExtractionField
            {...field}
            disabled={isDisabled}
            errors={validationErrors}
            form={form}
            isInputHighlighted={isInputHighlighted}
            isRequired={field.isRequired}
            key={field.id}
            setInForm={setInForm}
            validateField={validateField}
            onAdd={onAdd}
            onChange={handleChange}
            onRemove={onRemove}
          />
        );
      })}
    </>
  );
};

EditExtractionFieldsFormFieldsPart.propTypes = {
  form: PropTypes.object.isRequired,
  validationErrors: PropTypes.object.isRequired,
  setSchema: PropTypes.func.isRequired,
  setInForm: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  setForm: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  duplicatedFields: PropTypes.array.isRequired,
  setDuplicatedFields: PropTypes.func.isRequired,
  setDocumentExtractionFieldsTree: PropTypes.func.isRequired,
  documentExtractionFieldsTree: PropTypes.array.isRequired,
  setAiModelFieldToFillInForm: PropTypes.func,
  boundingBoxPopUpInfo: PropTypes.object,
  aiModelFieldToFillInForm: PropTypes.object,
  initialForm: PropTypes.object,
  documentTypeId: PropTypes.string,
  docId: PropTypes.string,
  isView: PropTypes.bool,
  isSkipDataLoading: PropTypes.bool,
  skipBooleanDefaults: PropTypes.bool,
  renderFields: PropTypes.func,
  ...WithLoadingPropTypes,
};

export default memo(EditExtractionFieldsFormFieldsPart);
